import {BackendApi} from '../../../../../api/api.ts'
import {fetchSalesInvoiceFile, postForSalesInvoiceFile, SalesInvoiceFile} from '../../../../../api/fetchSalesInvoiceFile.ts'
import {BackendAPITypes} from '../../../../../generated/blueprint.ts'
import {AddressFieldValue} from '../../../../../components/general/AddressField.tsx'
import {mapDropdownVATToVATCategory} from '../../../../../types/enums.ts'
import {DEFAULT_CURRENCY} from '../../../../../types/constants.ts'
import {InvoiceCustomer, InvoiceLineItem} from './types.ts'
import {isLineFilled, vatPercentageForBackend} from './utils/invoiceLines.ts'
import {isCountry} from '../../../../../types/countries.ts'
import type {Country, PaymentTerm} from '../../../../../generated/enums.ts';

export type SalesInvoiceResponse =
    BackendAPITypes.BACKENDPreHandleSyncApiV1MeBusinessGetSalesInvoiceGetResponseBody

export type SalesInvoiceNumbering =
    BackendAPITypes.BACKENDPreHandleSyncApiV1MeBusinessGetSalesInvoiceNumberingGetResponseBody

type DraftRequestBody =
    BackendAPITypes.BACKENDPreHandleSyncApiV1MeBusinessCreateSalesInvoiceDraftPostRequestBody

type CreditSalesInvoiceRequestBody =
    BackendAPITypes.BACKENDPreHandleSyncApiV1MeBusinessCreditSalesInvoicePostRequestBody

export type CreditSalesInvoiceLine = NonNullable<CreditSalesInvoiceRequestBody['lines']>[number]

export type InvoiceFile = SalesInvoiceFile

export type InvoiceFormState = {
    customer: InvoiceCustomer | null
    address: AddressFieldValue
    lines: InvoiceLineItem[]
    invoiceDate: string
    paymentTerm: PaymentTerm
    fiscalYear: number | null
    bankAccountId: string | null
    poNumber: string
    customerReference: string
    footerText: string
}

function emptyToNull(value: string): string | null {
    const trimmed = value.trim()
    return trimmed === '' ? null : trimmed
}

function customerCountry(value: string): Country {
    if (!isCountry(value)) {
        throw new Error('Selecteer een land voor de klant.')
    }
    return value
}

function persistedCustomerId(customer: InvoiceCustomer | null): string | null {
    if (!customer || customer.isAdHoc) return null
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(customer.id)
        ? customer.id
        : null
}

export function toDraftRequestBody(form: InvoiceFormState): DraftRequestBody {
    return {
        customer: {
            name: form.customer?.name.trim() ?? '',
            address: {
                street_name: form.address.street_name,
                house_number: form.address.house_number,
                city_name: form.address.city_name,
                postal_code: form.address.postal_code,
                country: customerCountry(form.address.country),
            },
            customer_id: persistedCustomerId(form.customer),
            email: emptyToNull(form.customer?.email ?? ''),
            cc_email: emptyToNull(form.customer?.ccEmail ?? ''),
            vat_number: emptyToNull(form.customer?.vatNumber ?? ''),
            address_extra_line: emptyToNull(form.customer?.addressExtraLine ?? ''),
        },
        invoice_date: form.invoiceDate,
        payment_term: form.paymentTerm,
        bank_account_id: form.bankAccountId ?? '',
        lines: form.lines.filter(isLineFilled).map(line => ({
            description: line.description.trim(),
            quantity: line.quantity ?? 0,
            unit: line.unit,
            unit_price_net: {
                signed_amount: Math.round((line.unitPrice ?? 0) * 100),
                currency: DEFAULT_CURRENCY,
            },
            vat_category: mapDropdownVATToVATCategory(line.vat),
            vat_percentage: vatPercentageForBackend(line.vat),
            catalogue_item_id: line.catalogueItemId,
            save_to_catalogue: line.saveToCatalogue,
            service_start_date: line.serviceStartDate ?? null,
            service_end_date: line.serviceEndDate ?? null,
        })),
        fiscal_year: form.fiscalYear,
        purchase_order_reference: emptyToNull(form.poNumber),
        customer_specific_reference: emptyToNull(form.customerReference),
        fixed_footer_text: emptyToNull(form.footerText),
    }
}

export async function createSalesInvoiceDraft(
    api: BackendApi, form: InvoiceFormState,
): Promise<SalesInvoiceResponse> {
    return await api.Me__business__createSalesInvoiceDraft(toDraftRequestBody(form))
}

export async function changeSalesInvoiceDraft(
    api: BackendApi, invoiceId: string, form: InvoiceFormState,
): Promise<SalesInvoiceResponse> {
    return await api.Me__business__changeSalesInvoiceDraft({
        ...toDraftRequestBody(form),
        invoice_id: invoiceId,
    })
}

export async function creditSalesInvoice(
    api: BackendApi,
    invoiceId: string,
    reason: string | null,
    lines: CreditSalesInvoiceLine[] | null,
): Promise<SalesInvoiceResponse> {
    return await api.Me__business__creditSalesInvoice({
        invoice_id: invoiceId,
        reason: reason,
        lines: lines,
    })
}

export async function issueSalesInvoice(
    api: BackendApi, invoiceId: string, numberPrefix: string | null,
): Promise<SalesInvoiceResponse> {
    return await api.Me__business__issueSalesInvoice({
        invoice_id: invoiceId,
        number_prefix: numberPrefix,
    })
}

export async function previewSalesInvoice(
    api: BackendApi, form: InvoiceFormState,
): Promise<InvoiceFile> {
    return await postForSalesInvoiceFile(
        api, 'me/business/preview-sales-invoice', toDraftRequestBody(form),
    )
}

export async function downloadSalesInvoicePdf(
    api: BackendApi, invoiceId: string,
): Promise<InvoiceFile> {
    return await fetchSalesInvoiceFile(
        api, 'me/business/download-sales-invoice-pdf', {invoice_id: invoiceId},
    )
}

export async function downloadSalesInvoiceDraftPdf(
    api: BackendApi, invoiceId: string,
): Promise<InvoiceFile> {
    return await fetchSalesInvoiceFile(
        api, 'me/business/download-sales-invoice-draft-pdf', {invoice_id: invoiceId},
    )
}

export async function getSalesInvoiceNumbering(
    api: BackendApi, year: number, prefix: string | null,
): Promise<SalesInvoiceNumbering> {
    return await api.Me__business__getSalesInvoiceNumbering(year, prefix)
}
